import { useCallback, useEffect, useRef, useState } from "react";
import api from "../api/axios";
import i18n from "i18next";
import { isSupportCode } from "../utils/supportIdentity";

// Keep the source identity for the loading stage, then hydrate public details.
// The request token also protects against late responses after closing/switching.
export default function useProfileCard(eventId) {
  const [state, setState] = useState({ participant: null, loading: false, error: false });
  const requestRef = useRef({ id: 0, controller: null });
  const sourceRef = useRef(null);

  const cancel = useCallback(() => {
    requestRef.current.id += 1;
    requestRef.current.controller?.abort();
    requestRef.current.controller = null;
  }, []);

  const close = useCallback(() => {
    cancel();
    sourceRef.current = null;
    setState({ participant: null, loading: false, error: false });
  }, [cancel]);

  useEffect(() => {
    close();
    return cancel;
  }, [eventId, close, cancel]);

  const open = useCallback(async (person) => {
    const identity = typeof person === "string" ? { code: person } : person;
    const code = String(identity?.code || "").trim().toUpperCase();
    if (!/^[A-Z0-9]{3}$/.test(code) || isSupportCode(code)) return;
    cancel();
    const source = { ...identity, code };
    sourceRef.current = source;
    setState({ participant: source, loading: true, error: false });
    const id = requestRef.current.id;
    const controller = new AbortController();
    requestRef.current.controller = controller;
    try {
      const url = eventId ? `/miniapp/participant-card/${eventId}/${code}/` : `/miniapp/profile-card/${code}/`;
      const response = await api.get(url, {
        signal: controller.signal,
        params: { language: i18n.resolvedLanguage || i18n.language },
      });
      if (requestRef.current.id !== id) return;
      setState({ participant: { ...source, ...response.data }, loading: false, error: false });
    } catch (error) {
      if (requestRef.current.id !== id || controller.signal.aborted) return;
      setState({ participant: source, loading: false, error: true });
    }
  }, [eventId, cancel]);

  const retry = useCallback(() => {
    if (sourceRef.current) void open(sourceRef.current);
  }, [open]);

  return { ...state, open, close, retry };
}
